import {Inject, Injectable, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {filter, firstValueFrom, map, Observable, Subject, take, takeUntil} from 'rxjs';
import {OAuthErrorEvent, OAuthService} from 'angular-oauth2-oidc';
import {Store} from '@ngrx/store';
import {login, logout} from '../../store/auth/actions/auth.actions';
import {AuthState} from '../../store/auth/reducers/auth.reducer';
import {environment} from '../../../environments/environment';
import {DOCUMENT} from '@angular/common';
import {Roles} from '../../enum/roles';
import {Route} from '../../enum/route';
import {AccessToken} from '../../model/access-token';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {UserLoadActions} from '../../store/user/actions/userActions';
import {CompanyAppState} from '../../store/company/reducers/company.reducer';
import {MyCompanyActions} from '../../store/company/actions/company.actions';
import {selectAuthenticated, selectAuthRelatedLoading} from '../../store/auth/selectors/auth.selectors';
import {Permissions} from '../../enum/permissions';
import {selectMyCompany} from '../../store/company/selectors/company.selectors';
import {AnnouncementsDialogComponent} from '../../components/dialogs/announcements-dialog/announcements-dialog.component';
import {LocalStorageItem} from '../../enum/local-storage-item';
import {TranslateService} from '@ngx-translate/core';
import {SmallDeviceDialogComponent} from '../../components/dialogs/small-device-dialog/small-device-dialog.component';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {selectUser} from '../../store/user/selectors/user.selectors';
import {Announcement} from '../../model/strapi/announcement/announcement';
import {StrapiService} from '../strapi-service/strapi.service';

@Injectable({providedIn: 'root'})
export class AuthService implements OnDestroy {

  private authenticationPromise: Promise<void> | null;

  private readonly ngUnsubscribe = new Subject();
  userRoles: Roles[] = [];
  userType: string;
  userIsRepresentative: boolean;
  userIsBoardMember: boolean;
  userIsEarlyBird: boolean;

  announcement$: Observable<Announcement> = this.strapiService.getAnnouncements().pipe(map(announcements => announcements[0]));


  constructor(private readonly strapiService: StrapiService,
              private readonly oauthService: OAuthService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly router: Router,
              private readonly dialog: MatDialog,
              private readonly authStore: Store<AuthState>,
              private readonly userStore: Store<UserAppState>,
              private readonly companyStore: Store<CompanyAppState>,
              private readonly translate: TranslateService,
              @Inject(DOCUMENT) private readonly document: Document) {
    // Synchronous assigning value null to ensure same Promise for multiple initAuth calls
    this.authenticationPromise = null;
    this.initOAuthEventDebugging();
    this.initOAuthEventSub();
    this.initTabEventListener();

    // Actual login procedure
    this.initAuthentication().then();

    if (this.oauthService.state && this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
      let stateUrl = this.oauthService.state;
      if (stateUrl.startsWith('/') === false) {
        stateUrl = decodeURIComponent(stateUrl);
      }
      console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
      this.router.navigateByUrl(stateUrl).then();
    }
  }

  public async initAuthentication(): Promise<void> {
    if (this.authenticationPromise === null) {
      this.authenticationPromise = (async () => {
        await this.oauthService.loadDiscoveryDocumentAndTryLogin();
        await this.dispatchCurrentUserAuthentication();
        if (this.authStore.select(selectAuthenticated)) {
          this.performRoutingLogic();
        }
      })();
    }
    return await this.authenticationPromise;
  }

  private dispatchCurrentUserAuthentication(): Promise<boolean> {
    let isAuthenticated: boolean = this.oauthService.hasValidAccessToken();
    if (isAuthenticated) {
      this.dispatchLoginActions(isAuthenticated);
    } else {
      this.authStore.dispatch(logout({isAuthenticated: false, roles: null}));
    }

    return firstValueFrom(this.authStore.select(selectAuthRelatedLoading).pipe(filter(value => {
      return value.isAuthenticated && !value.userLoading && !value.companyLoading;
    }))).then(() => {
      return isAuthenticated;
    });
  }

  private dispatchLoginActions(isAuthenticated: boolean) {
    let userRoles: Roles[] = [];
    let userPermissions: Permissions[] = [];
    let accessToken: AccessToken = this.getClaimsFromAccessToken as AccessToken;
    let stringAuthorities: string[] = accessToken.realm_authorities;
    stringAuthorities?.forEach(roleString => {
      if (Roles[roleString] !== undefined) {
        userRoles.push(Roles[roleString]);
      }
      if (Permissions[roleString] !== undefined) {
        userPermissions.push(Permissions[roleString]);
      }
    });
    this.userRoles = userRoles;
    this.authStore.dispatch(login({isAuthenticated: isAuthenticated, roles: userRoles, permissions: userPermissions}));
    this.userStore.dispatch(UserLoadActions.load({userId: accessToken.sub}));
    if(userRoles.length === 0) {
      console.warn('No roles found in access token, user might not be authenticated properly.');
      this.router.navigateByUrl(Route.ERROR_ROLE);
    } else{
      this.companyStore.dispatch(MyCompanyActions.load({companyName: accessToken.company[0].substring(accessToken.company[0].lastIndexOf('/') + 1)}));
    }
  }

  private performRoutingLogic() {
    let transmissionId: string = this.activatedRoute.snapshot.queryParamMap.get('id');
    let secret: string = this.activatedRoute.snapshot.queryParamMap.get('secret');
    let callback: string = this.activatedRoute.snapshot.queryParamMap.get('callback');

    let isValidTransmission: boolean = transmissionId !== null && secret !== null;
    let isCallback: boolean = callback !== null && callback == 'true';

    this.userStore.select(selectUser).subscribe(user => this.userType = user.type);
    this.userStore.select(selectUser).subscribe(user => this.userIsRepresentative = user.isRepresentative);
    this.userStore.select(selectUser).subscribe(user => this.userIsBoardMember = user.isBoardMember);
    this.userStore.select(selectUser).subscribe(user => this.userIsEarlyBird = user.isEarlyBird);

    if (isValidTransmission) {
      this.router.navigate([Route.INSERAT_ERSTELLEN], {queryParams: {'id': transmissionId, 'secret': secret}}).then();
    } else if (isCallback || (!window.location.pathname || window.location.pathname === '/')) {
      // if representative but not exchange nor CA
      if (this.userIsRepresentative && this.userType == null && !this.userRoles.includes(Roles.ROLE_CORPORATE_ADMIN) && !this.userRoles.includes(Roles.ROLE_DIRECT_CORPORATE_ADMIN)) {
        this.router.navigate([Route.BILL_BOARD]);
      }
      // if exchange roleclear
      else if (this.userType === Roles.ROLE_BROKER || this.userType === Roles.ROLE_UNDERWRITER || this.userType === Roles.ROLE_INSURER) {
        this.router.navigate([Route.INSERATE]).then();
      }
      // if CA / DCA / admin
      else if (this.userRoles.includes(Roles.ROLE_DIRECT_CORPORATE_ADMIN) || this.userRoles.includes(Roles.ROLE_CORPORATE_ADMIN)
        || this.userRoles.includes(Roles.ROLE_ADMIN)|| this.userRoles.includes(Roles.ROLE_ASSOCIATION_ADMIN)) {
        this.rerouteToMyCompany();
      } else {
        this.router.navigate([Route.INSERATE]).then();
      }
    }

    this.openDialogs();

  }

  private initTabEventListener() {
    // This is tricky, as it might cause race conditions (where access_token is set in another
    // tab before everything is said and done there.
    // Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      if (!this.oauthService.hasValidAccessToken()) {
        this.navigateHome();
      }
    });
  }

  private initOAuthEventDebugging() {
    // Useful for debugging:
    this.oauthService.events?.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(event => {
        if (event instanceof OAuthErrorEvent) {
          console.error('OAuthErrorEvent Object:', event);
        } else {
          console.warn('OAuthEvent Object:', event);
        }
      });
  }

  private initOAuthEventSub() {
    // OAuth Event Subscriptions
    this.oauthService.events?.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(e => {


        if (['token_received'].includes(e.type)) {
          this.oauthService.loadUserProfile().then();
        }

        if (['session_terminated', 'session_error'].includes(e.type)) {
          this.oauthService.revokeTokenAndLogout().then();
          this.navigateHome();
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.unsubscribe();
  }

  private navigateHome() {
    this.router.navigateByUrl('/').then();
  }

  public login(targetUrl?: string) {
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout() {
    this.oauthService.redirectUri = window.location.origin;
    this.oauthService.logOut();
    localStorage.setItem(LocalStorageItem.ARE_OPENED_LOGIN_DIALOGS, 'false');
  }

  public get getClaimsFromAccessToken() {
    return this.oauthService.getIdentityClaims();
  }

  public get getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  public get getUserDetailsFromIdToken() {
    return this.oauthService.getIdToken();
  }

  public get getGrantedScopes() {
    return this.oauthService.getGrantedScopes();
  }

  public resetPassword() {
    this.document.location.href = environment.oidcAuthority + environment.oidcPwResetPath;
  }

  public getUserIdFromAccessToken(): string {
    let accessToken: AccessToken = this.getClaimsFromAccessToken as AccessToken;
    return accessToken.sub;
  }

  public getCompanyNameFromAccessToken(): string {
    let accessToken: AccessToken = this.getClaimsFromAccessToken as AccessToken;
    return accessToken.company[0].substring(accessToken.company[0].lastIndexOf("/") + 1);
  }

  private rerouteToMyCompany() {
    this.companyStore.select(selectMyCompany).pipe(take(1)).subscribe(
      (company) => {
          let navigateUrl: string = `/${Route.COMPANY_PROFILE}/${company?.companyId}`;
          this.router.navigateByUrl(navigateUrl).then();
      }
    );
  }

  private openAnnouncementsDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    this.dialog.open(AnnouncementsDialogComponent, dialogConfig);
  }

  private openSmallDeviceDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      title: this.translate.instant('smallDeviceDialog.title'),
      content: this.translate.instant('smallDeviceDialog.content'),
    };
    this.userStore.select(selectUser).subscribe(user => {
      if(user.showSmallDeviceDialog){
        this.dialog.open(SmallDeviceDialogComponent, dialogConfig);
      }
    });
  }

  private openDialogs() {
    if (localStorage.getItem(LocalStorageItem.ARE_OPENED_LOGIN_DIALOGS) != 'true') {
      localStorage.setItem(LocalStorageItem.ARE_OPENED_LOGIN_DIALOGS, 'true');

      this.announcement$.pipe(take(1)).subscribe(announcement => {
        if (announcement) {
          this.userStore.select(selectUser).pipe(
            // this filter ensures that the user concludes the onboarding.
            // without it, this subscription will be consumed before user data are loaded
            filter(user => !!user && user.showAnnouncementDialog != null),
            take(1)
          ).subscribe(user => {
            if(user.showAnnouncementDialog){
              this.openAnnouncementsDialog();
            }
          });
        }
      });

      if (window.innerWidth < 832 || window.innerHeight < 832) this.openSmallDeviceDialog();
    }
  }
}
